import { z } from "zod";
import { PaginationQuery, ProfileSummary, Timestamp } from "../../common";

const EXAMPLE = {
  assetId: "0192a0c4-5d1e-7b3a-8f2c-4e6a9c1d3b57",
  profileId: "7c1f2a9e-4d3b-4f6a-8e2c-1b5d9a7f3e60",
  reporterId: "a2e4c6b8-0d1f-4a3b-9c5e-7f9a1b3d5e70",
  moderatorId: "5d7e9f1a-3b2c-4d8e-9f0a-6c4b2e8d1f73",
  caseId: "0192a1f0-3c4d-7e5f-9a1b-2c3d4e5f6a70",
  reportId: "0192a1f0-4d5e-7f60-8b2c-3d4e5f6a7b81",
  snapshotId: "0192a1f0-5e6f-7a71-9c3d-4e5f6a7b8c92",
  classificationId: "0192a1f0-6f70-7b82-8d4e-5f6a7b8c9da3",
  appealId: "0192a1f0-7081-7c93-9e5f-6a7b8c9d0eb4",
  banId: "0192a1f0-8192-7da4-8f60-7b8c9d0e1fc5",
  openedAt: "2026-10-12T16:20:00.000Z",
} as const;

/** Unicode code points, which is what Postgres `char_length()` and JS `[...text].length` count. */
function codePointLength(text: string): number {
  return [...text].length;
}

function boundedText(max: number) {
  return z
    .string()
    .trim()
    .refine(text => codePointLength(text) >= 1 && codePointLength(text) <= max, {
      message: `must be 1–${max} Unicode code points`,
    });
}

export const ModerationSubjectType = z
  .enum(["asset", "collection", "preset", "profile", "comment"])
  .meta({
    id: "ModerationSubjectType",
    description: "The kind of thing being reported, judged, or appealed.",
  });
export type ModerationSubjectType = z.infer<typeof ModerationSubjectType>;

export const ModerationSubject = z
  .strictObject({
    type: ModerationSubjectType,
    id: z.uuid().meta({
      description: "The item's id: an asset, collection, preset, comment, or profile id.",
      example: EXAMPLE.assetId,
    }),
  })
  .meta({
    id: "ModerationSubject",
    description: "The item a report, classification, or appeal is about.",
    example: { type: "asset", id: EXAMPLE.assetId },
  });
export type ModerationSubject = z.infer<typeof ModerationSubject>;

export const ModerationInputKind = z
  .enum([
    "profile_username",
    "profile_display_name",
    "profile_bio",
    "profile_region",
    "profile_avatar",
    "profile_banner",
    "asset_media",
    "asset_text",
    "collection_text",
    "preset_image",
    "preset_title",
    "comment_body",
    "live_chat",
    "live_action",
    "live_rule",
    "live_role",
    "live_title",
    "whole",
  ])
  .meta({
    id: "ModerationInputKind",
    description:
      "One input of an item, such as `profile_bio` or `asset_media`, or `whole` for an item judged as one piece: an asset or preset, file and text together, or any item whose inputs break a rule only together. The `live_` kinds are a live game's chat, actions, rules, role names, and title.",
  });
export type ModerationInputKind = z.infer<typeof ModerationInputKind>;

export const ModerationCategory = z
  .enum([
    "self_harm",
    "directing_self_harm",
    "sexual_content",
    "graphic/glorifying_atrocities",
    "graphic",
    "glorifying_atrocities",
    "identity_hate",
    "identity_ingame_violence",
    "personal_threats",
    "harassment",
    "pii/off_platform",
    "prompt_injection",
    "health_risk",
    "misrepresented_metadata",
    "spam",
    "appropriate",
  ])
  .meta({
    id: "ModerationCategory",
    description:
      "A category from the moderation policy. `appropriate` means nothing is wrong. Show each one with its wording from the moderation copy, never the key itself.",
  });
export type ModerationCategory = z.infer<typeof ModerationCategory>;

export const ModerationSeverity = z.int().min(1).max(5).meta({
  id: "ModerationSeverity",
  description:
    "How extreme the content is within the category: 1 is unlikely to bother reasonable adults, 2 could bother some people, 3 would disturb many, 4 would disturb almost everyone, and 5 crosses the category's bright line. `appropriate` has none. Severities 1 and 2 never lead to a consequence.",
  example: 4,
});

export const ReportReason = z
  .enum([
    "self_harm",
    "directing_self_harm",
    "sexual_content",
    "graphic/glorifying_atrocities",
    "graphic",
    "glorifying_atrocities",
    "identity_hate",
    "identity_ingame_violence",
    "personal_threats",
    "harassment",
    "pii/off_platform",
    "misrepresented_metadata",
    "spam",
    "other",
  ])
  .meta({
    id: "ReportReason",
    description:
      "Why the user is reporting the item. `identity_ingame_violence` applies only to presets; `graphic`, `glorifying_atrocities`, `misrepresented_metadata` and `other` only to assets, collections, and presets, and `graphic/glorifying_atrocities` only to profiles and comments. Copyright claims are not reports: show the DMCA copy instead of calling this route.",
  });
export type ReportReason = z.infer<typeof ReportReason>;

const PRESET_ONLY_REASONS: readonly ReportReason[] = ["identity_ingame_violence"];
const PUBLISHED_WORK_REASONS: readonly ReportReason[] = [
  "graphic",
  "glorifying_atrocities",
  "misrepresented_metadata",
  "other",
];
const PUBLISHED_WORK: readonly ModerationSubjectType[] = ["asset", "collection", "preset"];

/** Whether the policy lets a user give `reason` for an item of type `subjectType`. */
export function isReportReasonAllowed(
  subjectType: ModerationSubjectType,
  reason: ReportReason,
): boolean {
  if (PRESET_ONLY_REASONS.includes(reason)) return subjectType === "preset";
  if (PUBLISHED_WORK_REASONS.includes(reason)) return PUBLISHED_WORK.includes(subjectType);
  if (reason === "graphic/glorifying_atrocities") return !PUBLISHED_WORK.includes(subjectType);
  return true;
}

export const ReportCaseReviewer = z.enum(["heavy_ai", "moderator", "lead_moderator"]).meta({
  id: "ReportCaseReviewer",
  description:
    "Who an open case is waiting on: the heavy AI, the moderator queue, or the lead-moderator queue.",
});
export type ReportCaseReviewer = z.infer<typeof ReportCaseReviewer>;

export const ModerationWarning = z.enum(["small", "big", "crisis_resources"]).meta({
  id: "ModerationWarning",
  description:
    "How to tell the user. `small` is the inline warning, `big` the pop-up, and `crisis_resources` the welfare message with crisis support links.",
});
export type ModerationWarning = z.infer<typeof ModerationWarning>;

export const ModerationSurface = z
  .enum(["live_game", "profile", "social", "assets", "presets"])
  .meta({
    id: "ModerationSurface",
    description: "The part of Pax a ban covers, which names the abilities it removes.",
  });
export type ModerationSurface = z.infer<typeof ModerationSurface>;

export const ModeratedSurface = ModerationSurface.exclude(["live_game"]).meta({
  id: "ModeratedSurface",
  description:
    "A surface people moderate: `profile`, `assets` for assets and collections, `presets`, or `social` for comments.",
});
export type ModeratedSurface = z.infer<typeof ModeratedSurface>;

export const AppealStatus = z
  .enum([
    "awaiting_ai",
    "awaiting_escalation",
    "awaiting_human",
    "overturned",
    "upheld",
    "withdrawn",
  ])
  .meta({
    id: "AppealStatus",
    description:
      "Where the appeal stands. `awaiting_ai`: the heavy AI is judging it. `awaiting_escalation`: the AI upheld the refusal and the user can send it to a human or withdraw. `awaiting_human`: a moderator will judge it. `overturned` and `upheld` are final. `withdrawn` ends this filing, and the user can file again while the refusal is unchanged. The first three lock what the appeal is about.",
  });
export type AppealStatus = z.infer<typeof AppealStatus>;

export const AppealScope = z
  .enum([
    "draft",
    "profile_username",
    "profile_display_name",
    "profile_bio",
    "profile_region",
    "profile_avatar",
    "profile_banner",
    "collection_text",
    "ban",
  ])
  .meta({
    id: "AppealScope",
    description:
      "What an open appeal locks: `draft` for a whole asset or preset draft, `ban` for a surface ban, otherwise the one profile or collection field the refusal was about.",
  });

const ModerationId = (description: string, example: string) =>
  z.uuid().meta({ description, example });

export const ClassificationId = ModerationId(
  "The id of one classification. A refusal carries it, and appeals name the refusal by it.",
  EXAMPLE.classificationId,
);
export const AppealId = ModerationId("The appeal's id.", EXAMPLE.appealId);
export const ReportCaseId = ModerationId("The report case's id.", EXAMPLE.caseId);
const Count = z.int().min(0);

export const BanId = ModerationId("The surface ban's id.", EXAMPLE.banId);
export const SnapshotId = ModerationId(
  "The id of one frozen view of an input or item, exactly as a judge saw it.",
  EXAMPLE.snapshotId,
);

const Citation = boundedText(500).nullable().optional().meta({
  description:
    "The part of the input the category points at, such as one entity in a preset. Omit when the category applies to the input as a whole.",
  example: "Entity “Ostland”, description",
});

export const CategoryAssessment = z
  .discriminatedUnion("category", [
    z.strictObject({
      category: ModerationCategory.exclude(["appropriate"]),
      severity: ModerationSeverity,
      citation: Citation,
    }),
    z.strictObject({
      category: z.literal("appropriate"),
      severity: z.null().meta({ description: "Nothing is wrong, so there is no severity." }),
      citation: Citation,
    }),
  ])
  .meta({
    id: "CategoryAssessment",
    description: "One category a judge assigned, with its severity. `appropriate` has none.",
    example: { category: "identity_hate", severity: 4, citation: null },
  });
export type CategoryAssessment = z.infer<typeof CategoryAssessment>;

export const MandatoryTag = z
  .enum([
    "photo",
    "real_world_figure",
    "real_world_extremism",
    "revealing",
    "dead_bodies",
    "gore",
    "substances",
    "sensory",
  ])
  .meta({
    id: "MandatoryTag",
    description:
      "Something an asset's file shows that viewers can choose not to see. `photo`: a real photograph, or realistic enough to be one. `real_world_figure`: a real person. `real_world_extremism`: a hate symbol or a listed extremist group. `revealing`: a figure not covered from armpits to below the hips. `dead_bodies`: bodies that are or are likely dead. `gore`: a visible injury to a body. `substances`: alcohol, tobacco, drugs, or medication. `sensory`: flashing or a sudden loud noise.",
    example: "photo",
  });
export type MandatoryTag = z.infer<typeof MandatoryTag>;

export const MandatoryTags = z
  .array(MandatoryTag)
  .refine(tags => new Set(tags).size === tags.length, "Mandatory tags must not repeat.")
  .meta({
    description:
      "The mandatory tags the asset carries. They are kept apart from its tags, and searching never matches them.",
    example: ["photo"],
  });

export const RefusedCategory = z
  .strictObject({
    category: ModerationCategory,
    citation: z.string().nullable().meta({
      description: "The part of the input it points at, or null when it covers the whole input.",
    }),
  })
  .meta({
    id: "RefusedCategory",
    description: "A category that caused the refusal. Severity and points are never shown.",
  });

export const ModerationRefusal = z
  .strictObject({
    classificationId: ClassificationId,
    inputKind: ModerationInputKind,
    categories: z.array(RefusedCategory).min(1).meta({
      description:
        "The categories that caused the refusal, most severe first. Name every one of them in the small and big warnings.",
    }),
    warning: ModerationWarning.nullable().meta({
      description: "How to tell the user, or null when the refusal shows only its inline text.",
    }),
    appealable: z.boolean().meta({
      description:
        "Whether the user can appeal this refusal. False for non-text sexual content, for comments, and for content a moderator already ruled against. Offer Appeal only when true.",
    }),
  })
  .meta({
    id: "ModerationRefusal",
    description:
      "Why automod refused a submission. Routes that run automod return it as the data of a `MODERATION_REFUSED` error.",
    example: {
      classificationId: EXAMPLE.classificationId,
      inputKind: "profile_bio",
      categories: [{ category: "identity_hate", citation: null }],
      warning: "big",
      appealable: true,
    },
  });
export type ModerationRefusal = z.infer<typeof ModerationRefusal>;

export const BanLiftReason = z.enum(["appeal_overturned", "reclassified", "moderator"]).meta({
  id: "BanLiftReason",
  description:
    "Why a ban was lifted early: an overturned appeal or a reclassification left too few points in its window, or a moderator lifted it.",
});

/** Whether a ban can be appealed, and the appeal against it if there is one. */
const BanAppealState = {
  appealable: z.boolean().meta({
    description:
      "Whether the user can appeal the ban now: a live game or comment ban in force, under six months old, with no appeal open or decided.",
  }),
  appeal: z
    .strictObject({ id: AppealId, status: AppealStatus })
    .nullable()
    .meta({ description: "The latest appeal against the ban, or null." }),
};

export const SurfaceBan = z
  .strictObject({
    id: BanId,
    ...BanAppealState,
    surface: ModerationSurface,
    startsAt: Timestamp,
    earnedAt: Timestamp.meta({
      description:
        "When the points that caused the ban reached the threshold. Before `startsAt` when a moderator's later ruling on earlier content caused it, and then the ban copy names the day.",
    }),
    endsAt: Timestamp.nullable().meta({
      description: "When the ban ends, or null if it is permanent.",
    }),
    categories: z.array(ModerationCategory).meta({
      description:
        "The categories behind the points that caused the ban, for the ban copy's `{category_list}`. Empty when an appeal has since voided them all.",
    }),
  })
  .meta({
    id: "SurfaceBan",
    description: "The ban that stopped the request.",
    example: {
      id: EXAMPLE.banId,
      appealable: false,
      appeal: null,
      surface: "assets",
      startsAt: "2026-10-12T16:20:00.000Z",
      earnedAt: "2026-10-12T16:20:00.000Z",
      endsAt: "2026-10-15T16:20:00.000Z",
      categories: ["sexual_content"],
    },
  });
export type SurfaceBan = z.infer<typeof SurfaceBan>;

export const OpenAppeal = z
  .strictObject({
    appealId: AppealId,
    scope: AppealScope,
  })
  .meta({
    id: "OpenAppeal",
    description: "The open appeal that locks what the request tried to change.",
    example: { appealId: EXAMPLE.appealId, scope: "profile_username" },
  });

export const CreateReportRequest = z
  .strictObject({
    subject: ModerationSubject,
    reason: ReportReason,
    details: boundedText(1000).optional().meta({
      description:
        "What the user wrote about why. Required when `reason` is `other`; 1–1,000 Unicode code points. Moderators read it; the item's owner never does.",
      example: "The title calls it a medieval map, but it is a photo of a street.",
    }),
  })
  .superRefine((request, context) => {
    if (!isReportReasonAllowed(request.subject.type, request.reason)) {
      context.addIssue({
        code: "custom",
        path: ["reason"],
        message: `${request.reason} cannot be given for a ${request.subject.type}`,
      });
    }
    if (request.reason === "other" && request.details === undefined) {
      context.addIssue({
        code: "custom",
        path: ["details"],
        message: "details is required when reason is other",
      });
    }
  })
  .meta({
    id: "CreateReportRequest",
    example: {
      subject: { type: "asset", id: EXAMPLE.assetId },
      reason: "misrepresented_metadata",
      details: "The title calls it a medieval map, but it is a photo of a street.",
    },
  });
export type CreateReportRequest = z.infer<typeof CreateReportRequest>;

const AppealFields = {
  id: AppealId,
  status: AppealStatus,
  statement: z.string().nullable().meta({
    description: "What the user wrote in support of the appeal, or null.",
  }),
  openedAt: Timestamp,
  escalatedAt: Timestamp.nullable().meta({
    description: "When the user sent the appeal to a human, or null.",
  }),
  closedAt: Timestamp.nullable().meta({
    description: "When the appeal was decided or withdrawn, or null while it is open.",
  }),
};

export const RefusalAppeal = z
  .strictObject({
    kind: z.literal("refusal"),
    ...AppealFields,
    subject: ModerationSubject,
    scope: AppealScope.exclude(["ban"]),
    refusal: ModerationRefusal.meta({ description: "The refusal being appealed." }),
  })
  .meta({
    id: "RefusalAppeal",
    description: "An appeal against one refusal.",
    example: {
      kind: "refusal",
      id: EXAMPLE.appealId,
      status: "awaiting_ai",
      subject: { type: "profile", id: EXAMPLE.profileId },
      scope: "profile_bio",
      refusal: {
        classificationId: EXAMPLE.classificationId,
        inputKind: "profile_bio",
        categories: [{ category: "identity_hate", citation: null }],
        warning: "big",
        appealable: true,
      },
      statement: "It quotes a 1938 newspaper headline for a history preset.",
      openedAt: EXAMPLE.openedAt,
      escalatedAt: null,
      closedAt: null,
    },
  });

export const AppealedBan = z
  .strictObject({
    id: BanId,
    surface: ModerationSurface,
    escalationStep: z.int().min(1).meta({
      description: "Which ban on the surface it was within six months, from 1.",
    }),
    startsAt: Timestamp,
    endsAt: Timestamp.nullable().meta({ description: "Null on a permanent ban." }),
    pointsWindow: z.strictObject({ start: Timestamp, end: Timestamp }).meta({
      description: "The window whose points caused the ban, which the appeal judges again.",
    }),
    lifted: z
      .strictObject({ at: Timestamp, reason: BanLiftReason })
      .nullable()
      .meta({ description: "When and why the ban was lifted early, or null." }),
  })
  .meta({ id: "AppealedBan", description: "The ban a ban appeal is against." });

export const BanAppeal = z
  .strictObject({
    kind: z.literal("ban"),
    ...AppealFields,
    scope: z.literal("ban"),
    ban: AppealedBan,
  })
  .meta({
    id: "BanAppeal",
    description:
      "An appeal against a live game or comment ban. A moderator judges again everything the user wrote in the ban's points window.",
  });

export const Appeal = z.discriminatedUnion("kind", [RefusalAppeal, BanAppeal]).meta({
  id: "Appeal",
  description:
    "One appeal, as its appellant sees it: against one refusal, or against a live game or comment ban.",
});
export type Appeal = z.infer<typeof Appeal>;

export const AppealResponse = z.strictObject({ appeal: Appeal });

const AppealStatement = boundedText(1000).optional().meta({
  description:
    "Why the user thinks the refusal or ban is wrong, 1–1,000 Unicode code points. Only moderators read it.",
  example: "It quotes a 1938 newspaper headline for a history preset.",
});

export const CreateAppealRequest = z
  .union([
    z.strictObject({ classificationId: ClassificationId, statement: AppealStatement }),
    z.strictObject({ banId: BanId, statement: AppealStatement }),
  ])
  .meta({
    id: "CreateAppealRequest",
    description: "A refusal to appeal, by `classificationId`, or a ban, by `banId`.",
  });
export type CreateAppealRequest = z.infer<typeof CreateAppealRequest>;

export const AppealIdParams = z.strictObject({ appealId: AppealId });

export const ModerationSnapshot = z
  .strictObject({
    id: SnapshotId,
    subject: ModerationSubject,
    inputKind: ModerationInputKind,
    content: z.record(z.string(), z.unknown()).meta({
      description:
        "The input or item as it was judged. `inputs` holds each input keyed by its kind; `context` holds what surrounded it.",
    }),
    capturedAt: Timestamp,
  })
  .meta({ id: "ModerationSnapshot", description: "One frozen view of an input or item." });
export type ModerationSnapshot = z.infer<typeof ModerationSnapshot>;

export const SnapshotFile = z
  .strictObject({
    ref: z.string().min(1).meta({
      description:
        "What the snapshot names the file by: an upload id for a profile image or an asset's file, or a SHA-256 for a preset's image or video.",
      example: "k3v9x2mq7tfa",
    }),
    contentType: z.string().min(1).meta({
      description:
        "The file's media type. Show it with the element its `image/`, `video/`, or `audio/` prefix calls for.",
      example: "image/avif",
    }),
    url: z.url().meta({
      description:
        "Where to load it. A file that's private, such as anything automod refused, is signed and works for at least an hour.",
    }),
  })
  .meta({
    id: "SnapshotFile",
    description:
      "One file a snapshot names, in the size a moderator views it at: an image at most 2048 pixels on its longest side, a video, or a recording.",
  });
export type SnapshotFile = z.infer<typeof SnapshotFile>;

export const ModerationJudge = z.enum(["light_ai", "heavy_ai", "human", "precedent"]).meta({
  id: "ModerationJudge",
  description:
    "Who classified: `light_ai` is automod on save, `heavy_ai` the larger model on reports and appeals, `human` a moderator, and `precedent` a moderator's earlier ruling on the same content reused instead of calling the AI.",
});
export type ModerationJudge = z.infer<typeof ModerationJudge>;

export const ClassificationOutcome = z
  .strictObject({
    contentAction: z.enum(["blocked", "redacted", "removed"]).nullable().meta({
      description:
        "What happened to the content: `blocked` refused on save, `redacted` hidden where it was shown, `removed` taken down after a report. Null when it stayed up.",
    }),
    points: z.int().min(0).meta({
      description: "The points added to each account that answers for the item.",
    }),
    voided: z.boolean().meta({
      description: "Whether a later decision, such as an overturned appeal, voided it.",
    }),
  })
  .meta({
    id: "ClassificationOutcome",
    description: "What the policy did because of one classification.",
    example: { contentAction: "removed", points: 3, voided: false },
  });
export type ClassificationOutcome = z.infer<typeof ClassificationOutcome>;

export const ModerationClassification = z
  .strictObject({
    id: ClassificationId,
    snapshotId: SnapshotId,
    authorId: z.uuid().nullable().meta({
      description:
        "The one account that answers for what was judged, or null when none or several do.",
    }),
    judge: ModerationJudge,
    model: z.string().nullable().meta({ description: "The model an AI judge ran, or null." }),
    moderatorId: z
      .uuid()
      .nullable()
      .meta({ description: "The moderator, on a human classification." }),
    precedentClassificationId: ClassificationId.nullable().meta({
      description: "On a `precedent` classification, the earlier ruling it reused.",
    }),
    reportCaseId: ReportCaseId.nullable().meta({
      description: "The report case a moderator decided it in, or null.",
    }),
    appealId: AppealId.nullable().meta({ description: "The appeal it decided, or null." }),
    reviewedClassificationId: ClassificationId.nullable().meta({
      description:
        "On a moderator's review, the earlier classification of the same content it reviewed. A reclassification names the one it replaced.",
    }),
    categories: z.array(CategoryAssessment).min(1),
    outcome: ClassificationOutcome,
    decidedAt: Timestamp,
  })
  .meta({ id: "ModerationClassification", description: "One classification of one snapshot." });
export type ModerationClassification = z.infer<typeof ModerationClassification>;

export const PresetWords = z
  .strictObject({
    rules: z
      .array(z.strictObject({ name: z.string(), description: z.string(), body: z.string() }))
      .meta({
        description: "Each skill its game master follows: its name, when it matters, and its text.",
      }),
    lobby: z
      .array(z.strictObject({ name: z.string(), premise: z.string() }))
      .meta({ description: "Each perspective its lobby offers, by name and premise." }),
    map: z.array(z.string()).meta({
      description: "Every other distinct word its World holds, on its maps and anywhere else.",
    }),
  })
  .meta({
    id: "PresetWords",
    description: "Everything a preset's World says, as automod read it, apart from its page.",
  });
export type PresetWords = z.infer<typeof PresetWords>;

export const AppealReview = z
  .strictObject({
    snapshot: ModerationSnapshot.meta({ description: "What the refusal judged." }),
    label: z.string().nullable().meta({
      description:
        "The refused content in a few words: the start of its text, or a title. Null for a file.",
    }),
    files: z.array(SnapshotFile).meta({
      description:
        "Every file the snapshot names, such as a refused avatar or an asset's file. Empty for text.",
    }),
    words: PresetWords.nullable().meta({
      description:
        "A preset's words, read from the World its snapshot names. Null for anything but a preset, or when that World can't be read.",
    }),
    classifications: z.array(ModerationClassification).meta({
      description:
        "Every classification of that snapshot so far, oldest first: the refusal, then the AI's.",
    }),
    people: z.array(ProfileSummary).meta({
      description: "The moderators who judged it, to name them.",
    }),
  })
  .meta({ id: "AppealReview", description: "What a moderator needs to decide an appeal." });

export const BanWindowInput = z
  .strictObject({
    snapshotId: SnapshotId,
    inputKind: ModerationInputKind,
    text: z.string().meta({ description: "What the user wrote, as automod judged it." }),
    context: z.array(z.string()).meta({
      description:
        "What came just before, to read it by: their earlier lines in the game, or the comment it replies to.",
    }),
    where: z.string().nullable().meta({
      description: "The game or preset it was written in, by title, or null when that is gone.",
    }),
    classifications: z.array(ModerationClassification).meta({
      description: "Every judgement of it, oldest first: automod's, then any moderator's.",
    }),
    points: Count.meta({ description: "The points it counts for now." }),
  })
  .meta({
    id: "BanWindowInput",
    description: "One thing the user wrote on the banned surface during the ban's points window.",
  });
export type BanWindowInput = z.infer<typeof BanWindowInput>;

export const BanAppealReview = z
  .strictObject({
    inputs: z.array(BanWindowInput).meta({
      description:
        "Everything the user wrote on the surface in the ban's points window that automod judged, oldest first, including what earned no points.",
    }),
    banAt: z
      .int()
      .min(1)
      .meta({ description: "Points within the window at which the surface bans." }),
    people: z.array(ProfileSummary).meta({
      description: "The moderators who judged any of it, to name them.",
    }),
  })
  .meta({ id: "BanAppealReview", description: "What a moderator needs to decide a ban appeal." });

const Appellant = ProfileSummary.nullable().meta({
  description: "The user who appealed, or null once their account is deleted.",
});

export const AppealForReview = z
  .discriminatedUnion("kind", [
    RefusalAppeal.extend({ appellant: Appellant, review: AppealReview }),
    BanAppeal.extend({ appellant: Appellant, review: BanAppealReview }),
  ])
  .meta({
    id: "AppealForReview",
    description: "An appeal with everything a moderator decides it from.",
  });
export type AppealForReview = z.infer<typeof AppealForReview>;

const SurfaceFilter = {
  surface: ModeratedSurface.optional().meta({
    description: "Only this surface. Omit for every surface the caller moderates.",
  }),
  subjectType: ModerationSubjectType.optional().meta({
    description: "Only this kind of item. Omit for every kind the caller moderates.",
  }),
};

export const ListAppealReviewsQuery = PaginationQuery.extend({
  state: z.enum(["awaiting_human", "decided"]).default("awaiting_human").meta({
    description:
      "`awaiting_human` for the queue, longest-waiting first; `decided` for appeals a moderator upheld or overturned, most recently decided first.",
  }),
  ...SurfaceFilter,
  surface: ModerationSurface.optional().meta({
    description:
      "Only this surface, `live_game` included for its ban appeals. Omit for every surface the caller works appeals on.",
  }),
});
export type ListAppealReviewsQuery = z.infer<typeof ListAppealReviewsQuery>;
export const ListAppealReviewsResponse = z.strictObject({
  appeals: z.array(AppealForReview),
  nextCursor: z.string().nullable(),
});

const SubjectFilter = {
  subjectType: ModerationSubjectType.optional().meta({
    description: "Only this kind of item. Send with `subjectId` to ask about one item.",
  }),
  subjectId: z.uuid().optional().meta({ description: "Only this item." }),
};

export const ListAppealsQuery = PaginationQuery.extend({
  state: z.enum(["open", "closed"]).optional().meta({
    description:
      "`open` for appeals still in progress, which lock what they are about; `closed` for decided and withdrawn ones. Omit for both.",
  }),
  ...SubjectFilter,
});
export type ListAppealsQuery = z.infer<typeof ListAppealsQuery>;

export const ListAppealsResponse = z.strictObject({
  appeals: z.array(Appeal).meta({ description: "The user's appeals, newest first." }),
  nextCursor: z.string().nullable(),
});

export const AppealableRefusal = z
  .strictObject({
    subject: ModerationSubject,
    refusal: ModerationRefusal,
    refusedAt: Timestamp,
  })
  .meta({
    id: "AppealableRefusal",
    description:
      "A refusal the user can still appeal: appealable, unchanged since, at most six months old, and with no open or decided appeal.",
  });

export const ListRefusalsQuery = z.strictObject(SubjectFilter);
export type ListRefusalsQuery = z.infer<typeof ListRefusalsQuery>;

export const ListRefusalsResponse = z.strictObject({
  refusals: z.array(AppealableRefusal).meta({
    description: "Every refusal the user can still appeal, newest first.",
  }),
});

export const DecideAppealRequest = z
  .union([
    z.strictObject({
      categories: z.array(CategoryAssessment).min(1).meta({
        description:
          "The moderator's categorization of the refused snapshot. The policy decides from it whether the refusal stands.",
      }),
      mandatoryTags: MandatoryTags.optional().meta({
        description:
          "Every mandatory tag the refused asset file calls for, on a refusal that holds one. Leave it out to keep the refusal's. An overturned draft takes them on when it is published.",
      }),
    }),
    z.strictObject({
      inputs: z
        .array(
          z.strictObject({
            snapshotId: SnapshotId,
            categories: z.array(CategoryAssessment).min(1),
          }),
        )
        .meta({
          description:
            "On a ban appeal, the inputs from the window the moderator labels differently from automod, each with its categories. Every other input keeps automod's label. The ban stands only if the window's points still reach the ban threshold.",
        }),
    }),
  ])
  .meta({
    id: "DecideAppealRequest",
    description: "`categories` decides a refusal appeal; `inputs` decides a ban appeal.",
    example: { categories: [{ category: "appropriate", severity: null }] },
  });
export type DecideAppealRequest = z.infer<typeof DecideAppealRequest>;

const Removal = z
  .strictObject({
    inputKind: ModerationInputKind,
    categories: z.array(RefusedCategory).min(1).meta({
      description: "The categories that caused the removal. Name every one of them.",
    }),
    decidedBy: z.enum(["automod", "moderator"]),
    removedAt: Timestamp,
  })
  .meta({
    id: "ModerationRemoval",
    description: "Content taken down after a report.",
  });

export const ItemModeration = z
  .strictObject({
    refusal: ModerationRefusal.nullable().meta({
      description:
        "The refusal standing on the item: a refused draft, or the latest refused attempt at a collection's text. Null when there is none.",
    }),
    appeal: z.strictObject({ id: AppealId, status: AppealStatus }).nullable().meta({
      description: "The item's most recent appeal, or null if it was never appealed.",
    }),
    removal: Removal.nullable().meta({
      description: "Set when the item was taken down after a report.",
    }),
  })
  .meta({
    id: "ItemModeration",
    description:
      "An item's moderation state, returned with the item to its owner only. Show it on the item itself: the refusal with Edit and Appeal, the appeal's progress, or the removal banner.",
    example: {
      refusal: {
        classificationId: EXAMPLE.classificationId,
        inputKind: "whole",
        categories: [{ category: "misrepresented_metadata", citation: null }],
        warning: "small",
        appealable: true,
      },
      appeal: { id: EXAMPLE.appealId, status: "awaiting_human" },
      removal: null,
    },
  });
export type ItemModeration = z.infer<typeof ItemModeration>;

export const ProfileModeration = z
  .strictObject({
    appeals: z.array(Appeal).meta({
      description:
        "The profile's open appeals, newest first. Each locks the field its `scope` names until it is decided or withdrawn.",
    }),
  })
  .meta({
    id: "ProfileModeration",
    description:
      "A profile's moderation state, returned with the profile to its owner only. Show each open appeal on the field it locks. A refused change is offered for appeal only in the refusal itself.",
  });
export type ProfileModeration = z.infer<typeof ProfileModeration>;

export const AccountStanding = z
  .strictObject({
    surfaces: z
      .array(
        z.strictObject({
          surface: ModerationSurface,
          ban: SurfaceBan.pick({
            id: true,
            appealable: true,
            appeal: true,
            startsAt: true,
            earnedAt: true,
            endsAt: true,
          })
            .nullable()
            .meta({
              description:
                "The ban in force on this surface, or null, and whether it can be appealed. `endsAt` is null on a permanent ban.",
            }),
          nearBan: z.boolean().meta({
            description:
              "Whether the account's points on this surface are close to a ban. Show the approaching-ban copy when true.",
          }),
          categories: z.array(ModerationCategory).min(1).meta({
            description:
              "The categories behind the ban or the points counting now, for the copy's `{category_list}`.",
          }),
        }),
      )
      .meta({
        description:
          "Every surface where the account is banned or close to a ban. Surfaces in good standing are left out.",
      }),
  })
  .meta({
    id: "AccountStanding",
    description: "Where the user's account stands with moderation right now.",
    example: {
      surfaces: [
        {
          surface: "assets",
          ban: { startsAt: EXAMPLE.openedAt, endsAt: "2026-10-15T16:20:00.000Z" },
          nearBan: false,
          categories: ["identity_hate"],
        },
      ],
    },
  });

export const ReportCaseSummary = z
  .strictObject({
    id: ReportCaseId,
    subject: ModerationSubject,
    awaiting: ReportCaseReviewer.nullable().meta({
      description: "Who the case is waiting on, or null once it is closed.",
    }),
    label: z.string().nullable().meta({
      description:
        "The item as it was last reported, in a few words: an asset's, collection's, or preset's title, a profile's display name, or the start of a comment. Null for an untitled asset.",
      example: "Ostland, 1938",
    }),
    preview: SnapshotFile.nullable().meta({
      description:
        "A small picture of the item as it was last reported, for a list row: an image or basemap asset's file, or a profile's avatar. Null for anything else.",
    }),
    responsibleUserIds: z.array(z.uuid()).meta({
      description:
        "The accounts that answer for the item, read when the case opened: its author, owner, or collaborators.",
    }),
    reasons: z.array(ReportReason).min(1).meta({ description: "Every distinct reason given." }),
    reportCount: z.int().min(1),
    openedAt: Timestamp,
    sentToLead: z
      .strictObject({
        moderatorId: z.uuid().nullable().meta({
          description: "The moderator who sent it, or null once their account is deleted.",
        }),
        at: Timestamp,
      })
      .nullable()
      .meta({
        description:
          "Set once a moderator sent the case to the lead-moderator queue instead of deciding it.",
      }),
    closedAt: Timestamp.nullable(),
    decision: z
      .strictObject({
        moderatorIds: z.array(z.uuid()).meta({
          description: "The moderators who decided it, usually one.",
        }),
        actionTaken: z.boolean().meta({
          description: "Whether the policy took the item down or added points.",
        }),
      })
      .nullable()
      .meta({ description: "On a closed case, who decided it and whether anything happened." }),
  })
  .meta({
    id: "ReportCaseSummary",
    example: {
      id: EXAMPLE.caseId,
      subject: { type: "asset", id: EXAMPLE.assetId },
      label: "Ostland, 1938",
      awaiting: "moderator",
      responsibleUserIds: [EXAMPLE.profileId],
      reasons: ["misrepresented_metadata", "spam"],
      reportCount: 3,
      openedAt: EXAMPLE.openedAt,
      sentToLead: null,
      closedAt: null,
      decision: null,
    },
  });
export type ReportCaseSummary = z.infer<typeof ReportCaseSummary>;

export const ListReportCasesQuery = PaginationQuery.extend({
  state: z.enum(["open", "closed"]).default("open").meta({
    description:
      "`open` for a human queue, oldest first; `closed` for decided cases, most recently decided first.",
  }),
  awaiting: z.enum(["moderator", "lead_moderator"]).default("moderator").meta({
    description:
      "Which human queue to list, when `state` is `open`. Each kind of item is listed only to staff who can work it in that queue. Closed cases are listed to anyone who can work the moderator queue.",
  }),
  ...SurfaceFilter,
  reason: ReportReason.optional().meta({
    description: "List only cases where at least one report gave this reason.",
  }),
});
export type ListReportCasesQuery = z.infer<typeof ListReportCasesQuery>;

export const ListReportCasesResponse = z.strictObject({
  cases: z.array(ReportCaseSummary),
  people: z.array(ProfileSummary).meta({
    description:
      "The accounts that answer for the items and the moderators who sent the cases to a lead or decided them, to name them. A deleted account is left out.",
  }),
  nextCursor: z.string().nullable(),
});

export const ReportCaseIdParams = z.strictObject({ caseId: ReportCaseId });

export const ReportCaseDetail = z
  .strictObject({
    case: ReportCaseSummary,
    reports: z.array(
      z.strictObject({
        id: z.uuid().meta({ example: EXAMPLE.reportId }),
        reporterId: z.uuid().nullable().meta({
          description: "Who reported it, or null once that account is deleted.",
          example: EXAMPLE.reporterId,
        }),
        reason: ReportReason,
        details: z.string().nullable(),
        snapshotId: SnapshotId.meta({ description: "The item as this reporter saw it." }),
        createdAt: Timestamp,
      }),
    ),
    snapshots: z.array(ModerationSnapshot).meta({
      description:
        "Every distinct view of the item the reports and classifications refer to, oldest first.",
    }),
    classifications: z.array(ModerationClassification).meta({
      description: "Classifications made for this case so far, such as the heavy AI's.",
    }),
    history: z
      .array(
        z.strictObject({
          snapshotId: SnapshotId.meta({ description: "The reported snapshot the input is in." }),
          inputKind: ModerationInputKind,
          classifications: z.array(ModerationClassification).min(1).meta({
            description: "Every judgement of exactly this content, oldest first.",
          }),
        }),
      )
      .meta({
        description:
          "What judges said about the reported inputs outside this case, such as automod's verdict when each was saved and earlier moderator rulings on the same content. Inputs nobody judged before are left out.",
      }),
    people: z.array(ProfileSummary).meta({
      description:
        "The reporters, the accounts in `responsibleUserIds`, the moderator who sent the case to a lead, and the moderators who judged the item, here or before, to name them. A deleted account is left out.",
    }),
    presetSlug: z.string().nullable().meta({
      description:
        "The slug of the preset page the item is on, to open it there: a preset's own, or the one a comment is on. Null for anything else.",
    }),
    files: z.array(SnapshotFile).meta({
      description:
        "Every file the snapshots name, once each, including one the user has since replaced, cleared, or deleted.",
    }),
  })
  .meta({
    id: "ReportCaseDetail",
    description: "One case with everything a moderator decides it from.",
  });
export type ReportCaseDetail = z.infer<typeof ReportCaseDetail>;

export const DecideReportCaseRequest = z
  .strictObject({
    classifications: z
      .array(
        z.strictObject({
          snapshotId: SnapshotId.meta({
            description:
              "One of the case's reported snapshots: the item as a reporter saw it. A case whose item changed between reports has several.",
          }),
          inputKind: ModerationInputKind.meta({
            description:
              "One of the snapshot's inputs, or `whole` for a violation only the inputs together make. An asset or preset is only labeled `whole`: its file and text together.",
          }),
          categories: z.array(CategoryAssessment).min(1),
          mandatoryTags: MandatoryTags.optional().meta({
            description:
              "Every mandatory tag the asset's file calls for, when the input judged holds it. The asset takes them on. Leave it out to keep the ones the classification under review gave.",
          }),
        }),
      )
      .min(1)
      .meta({
        description:
          "One categorization per input the moderator judged. A single `whole` entry of `appropriate` keeps the item as it is. A reported reason the moderator doesn't find is left out rather than given a severity.",
      }),
    takeDownWholeAsset: z.boolean().optional().meta({
      description:
        "On an asset case, `true` takes the whole asset down if the policy removes the reported version: every version leaves public view, the asset leaves discovery and every collection, and only its owner can still read it. Without it, only the reported version is removed. Any other case refuses it.",
      example: false,
    }),
  })
  .meta({
    id: "DecideReportCaseRequest",
    example: {
      classifications: [
        {
          snapshotId: EXAMPLE.snapshotId,
          inputKind: "whole",
          categories: [
            { category: "misrepresented_metadata", severity: 4 },
            { category: "spam", severity: 1 },
          ],
        },
      ],
    },
  });
export type DecideReportCaseRequest = z.infer<typeof DecideReportCaseRequest>;

export const JudgedInput = z
  .strictObject({
    snapshot: ModerationSnapshot.meta({ description: "The input exactly as its judges saw it." }),
    label: z.string().nullable().meta({
      description:
        "The input in a few words: the start of its text, or an item's title. Null for a file.",
    }),
    classifications: z.array(ModerationClassification).min(1).meta({
      description: "Every judgement of the input, oldest first.",
    }),
  })
  .meta({
    id: "JudgedInput",
    description: "One input with every judge's classification of it, so they can be compared.",
  });
export type JudgedInput = z.infer<typeof JudgedInput>;

export const ListJudgedInputsQuery = PaginationQuery.extend({
  surface: ModeratedSurface.optional().meta({
    description: "Only this surface's inputs. Omit for every surface the caller moderates.",
  }),
  judge: ModerationJudge.optional().meta({ description: "Only inputs this judge classified." }),
  subjectType: ModerationSubjectType.optional().meta({
    description: "Only this kind of item on the surface. Omit for every kind.",
  }),
  category: ModerationCategory.optional().meta({
    description: "Only inputs some judge put in this category, at any severity.",
  }),
  agreement: z.enum(["agreed", "disagreed"]).optional().meta({
    description:
      "Only inputs a moderator reviewed after an AI, where the moderator's latest review agreed or disagreed with the AI classification it reviewed on whether the policy acts.",
  }),
});
export type ListJudgedInputsQuery = z.infer<typeof ListJudgedInputsQuery>;

export const ListJudgedInputsResponse = z.strictObject({
  inputs: z.array(JudgedInput).meta({ description: "Most recently judged first." }),
  people: z.array(ProfileSummary).meta({
    description: "The moderators who judged them and the users who answer for them, to name them.",
  }),
  nextCursor: z.string().nullable(),
});

export const SurfaceAccess = z
  .strictObject({
    moderate: z.boolean().meta({
      description: "Works the moderator queue and appeals, and reads users' records.",
    }),
    leadModerate: z.boolean().meta({
      description: "Works the lead-moderator queue and reclassifies judgements.",
    }),
    viewStats: z.boolean().meta({ description: "Reads the surface's stats." }),
  })
  .meta({ id: "SurfaceAccess", description: "What the caller can do on one surface." });

export const ModerationSurfaceOverview = z
  .strictObject({
    surface: ModerationSurface.meta({
      description:
        "A moderated surface, or `live_game` for someone who decides its ban appeals or reads its stats.",
    }),
    access: SurfaceAccess,
    waiting: z
      .strictObject({
        reports: Count.meta({ description: "Open cases in the moderator queue." }),
        leadReports: Count.meta({ description: "Open cases in the lead-moderator queue." }),
        appeals: Count.meta({ description: "Appeals waiting on a moderator." }),
      })
      .nullable()
      .meta({
        description: "What waits on people, or null when the caller can only read stats.",
      }),
  })
  .meta({
    id: "ModerationSurfaceOverview",
    description: "One surface the caller moderates, with what waits there.",
    example: {
      surface: "profile",
      access: { moderate: true, leadModerate: false, viewStats: false },
      waiting: { reports: 12, leadReports: 1, appeals: 3 },
    },
  });
export type ModerationSurfaceOverview = z.infer<typeof ModerationSurfaceOverview>;

export const ListModerationSurfacesResponse = z.strictObject({
  surfaces: z.array(ModerationSurfaceOverview).meta({
    description:
      "Every surface the caller holds a moderation permission on: profile, assets, presets, then social.",
  }),
});

export const StatsParams = z.strictObject({
  surface: ModerationSurface.meta({
    description:
      "The surface to count: a moderated surface, or `live_game`, which only automod judges.",
  }),
});

export const ModerationStatsQuery = z.strictObject({
  days: z.coerce.number().int().min(1).max(365).default(30).meta({
    description: "How many days back to count, up to now.",
  }),
});
export type ModerationStatsQuery = z.infer<typeof ModerationStatsQuery>;

const Hours = z.number().min(0).nullable();

/** A count over the period, beside the same count over the period of the same length before it. */
const Compared = z.strictObject({
  count: Count,
  previous: Count.meta({ description: "The same count over the period just before." }),
});

export const BanSurface = z.enum([...ModerationSurface.options, "full_site"]).meta({
  id: "BanSurface",
  description: "A surface a ban covers, or `full_site` for the whole site.",
});
export type BanSurface = z.infer<typeof BanSurface>;

export const AutomodFailureReason = z
  .enum(["timeout", "gateway_refusal", "vendor_error", "invalid_output"])
  .meta({
    id: "AutomodFailureReason",
    description:
      "Why an automod check couldn't finish: it ran out of time, the model gateway refused it, the model's vendor failed, or its answer couldn't be read.",
  });
export type AutomodFailureReason = z.infer<typeof AutomodFailureReason>;

const LabeledCategory = ModerationCategory.exclude(["appropriate"]);

export const ModerationStats = z
  .strictObject({
    surface: ModerationSurface,
    since: Timestamp.meta({ description: "The start of the period; it runs to now." }),
    previousSince: Timestamp.meta({
      description:
        "The start of the period of the same length before it, which `previous` counts cover.",
    }),
    days: z
      .array(
        z.strictObject({
          day: z.iso.date().meta({ description: "A UTC day." }),
          passed: Count.meta({ description: "Automod labels that day that were appropriate." }),
          flagged: Count.meta({
            description:
              "Automod labels that day with another category that the policy didn't act on.",
          }),
          acted: Count.meta({
            description:
              "Automod labels that day the policy acted on: refused, or redacted in a live game.",
          }),
          reports: Count.meta({ description: "Reports filed that day." }),
          waiting: Count.meta({
            description:
              "Cases waiting on a moderator or a lead at the end of that day, or now for today.",
          }),
        }),
      )
      .meta({ description: "Each day of the period, oldest first." }),
    automod: z.strictObject({
      checked: Compared.meta({ description: "Inputs automod labeled." }),
      flagged: Compared.meta({
        description:
          "Of those, how many it gave a category other than `appropriate`, at any severity.",
      }),
      acted: Compared.meta({
        description: "Of those, how many the policy acted on: refused, or redacted in a live game.",
      }),
      failed: Compared.meta({
        description:
          "Checks that couldn't finish, so the submission wasn't saved or the input wasn't judged.",
      }),
      failures: z
        .array(z.strictObject({ reason: AutomodFailureReason, count: Count }))
        .meta({ description: "Failed checks in the period by why they failed, most first." }),
      medianLatencyMs: z.number().min(0).nullable(),
      p95LatencyMs: z.number().min(0).nullable(),
    }),
    categories: z
      .array(
        z.strictObject({
          category: LabeledCategory,
          flagged: Count.meta({ description: "Automod labels in the period with this category." }),
          severities: z.array(Count).length(5).meta({
            description: "Those labels by the severity given to this category, from 1 to 5.",
          }),
          acted: Count.meta({ description: "Of those labels, how many the policy acted on." }),
          reviewed: Count.meta({
            description:
              "Moderator decisions in the period, in a case or an appeal, on automod labels with this category, or on automod labels that passed content a moderator gave this category.",
          }),
          agreed: Count.meta({
            description:
              "Of those, how many lead to the same result as automod's label: both act, or neither does.",
          }),
          overRefused: Count.meta({
            description: "Of those, how many automod's label acts on and the moderator's doesn't.",
          }),
          missed: Count.meta({
            description: "Of those, how many the moderator's label acts on and automod's didn't.",
          }),
          changedTo: z.array(z.strictObject({ category: ModerationCategory, count: Count })).meta({
            description:
              "Where moderators reviewed an automod label with this category and didn't keep it, the categories they gave instead, most first.",
          }),
        }),
      )
      .meta({ description: "Each category the surface's policy uses, most flagged first." }),
    together: z
      .strictObject({
        labeled: z
          .array(z.strictObject({ category: LabeledCategory, inputs: Count }))
          .meta({ description: "Inputs whose latest label in the period has each category." }),
        pairs: z
          .array(
            z.strictObject({ category: LabeledCategory, with: LabeledCategory, inputs: Count }),
          )
          .meta({
            description:
              "Inputs whose latest label has both categories, once per pair, with `category` first in the policy's order.",
          }),
      })
      .meta({
        description:
          "Which categories are given together on the same input, by each input's latest label in the period: a moderator's where there is one, else automod's.",
      }),
    reports: z
      .strictObject({
        received: Compared.meta({ description: "Reports users filed." }),
        byReason: z.array(z.strictObject({ reason: ReportReason, count: Count })).meta({
          description: "Reports received, by reason, most first.",
        }),
        waiting: Count.meta({
          description: "Cases waiting on a moderator or a lead now, whenever they were opened.",
        }),
        oldestWaitingSince: Timestamp.nullable().meta({
          description:
            "When the longest-waiting of those cases was first reported, or null if none wait.",
        }),
        decided: Compared.meta({ description: "Cases a moderator decided." }),
        actioned: Count.meta({
          description:
            "Of the cases decided in the period, how many the policy took down or added points for.",
        }),
        medianHoursToDecision: Hours.meta({
          description: "How long decided cases took, from the first report to the decision.",
        }),
        p90HoursToDecision: Hours.meta({ description: "The same, for the slowest tenth." }),
      })
      .nullable()
      .meta({ description: "Null on `live_game`, which has no reports." }),
    appeals: z
      .strictObject({
        filed: Compared.meta({ description: "Appeals users filed." }),
        waiting: Count.meta({ description: "Appeals waiting on a moderator now." }),
        decided: Count.meta({
          description: "Appeals a moderator upheld or overturned in the period.",
        }),
        overturned: Count.meta({
          description: "Of those, how many the moderator overturned: refusals automod got wrong.",
        }),
        medianHoursToDecision: Hours.meta({
          description: "How long decided appeals took, from filing to the decision.",
        }),
        p90HoursToDecision: Hours.meta({ description: "The same, for the slowest tenth." }),
      })
      .nullable()
      .meta({ description: "Null on `live_game`, whose refusals can't be appealed." }),
    bans: z.strictObject({
      placed: Compared.meta({ description: "Bans that started in the period." }),
      byStep: z.array(z.strictObject({ step: z.int().min(1), count: Count })).meta({
        description:
          "Bans placed in the period by which ban on the surface each was within the escalation window, from 1.",
      }),
      permanent: Count.meta({ description: "Of the bans placed, how many have no end." }),
      inForce: Count.meta({ description: "Bans in force now, whenever they started." }),
      lifted: z
        .array(z.strictObject({ reason: BanLiftReason, count: Count }))
        .meta({ description: "Bans lifted early in the period, by why." }),
      recent: z
        .array(
          z.strictObject({
            userId: z.uuid(),
            step: z.int().min(1).meta({
              description: "Which ban on the surface it was within the escalation window, from 1.",
            }),
            startsAt: Timestamp,
            endsAt: Timestamp.nullable().meta({ description: "Null on a permanent ban." }),
            lifted: z
              .strictObject({ at: Timestamp, reason: BanLiftReason })
              .nullable()
              .meta({ description: "When and why it was lifted early, or null." }),
          }),
        )
        .meta({ description: "Up to ten bans placed in the period, newest first, to name who." }),
      points: Count.meta({ description: "Points given in the period and not since voided." }),
      pointedPeople: Count.meta({ description: "How many people those points went to." }),
      nearBan: z.array(z.strictObject({ userId: z.uuid(), points: Count })).meta({
        description:
          "Up to five people not banned here whose points in the current window are close to a ban, most points first.",
      }),
      nearBanCount: Count.meta({ description: "How many people are close to a ban in all." }),
      banAt: z
        .int()
        .min(1)
        .meta({ description: "Points within the window at which the surface bans." }),
      across: z
        .strictObject({
          banned: Count.meta({
            description:
              "People banned on this surface within the escalation window, whatever the period.",
          }),
          alsoBanned: z.array(z.strictObject({ surface: BanSurface, people: Count })).meta({
            description:
              "Of those, how many were also banned on each other surface within the same window, every other surface listed.",
          }),
        })
        .meta({
          description:
            "Whether people banned here are banned elsewhere too, over the escalation window rather than the period.",
        }),
    }),
    moderators: z
      .array(
        z.strictObject({
          moderatorId: z.uuid(),
          cases: Count.meta({ description: "Report cases they decided." }),
          appeals: Count.meta({ description: "Appeals they decided." }),
          relabels: Count.meta({
            description: "Labels they changed outside a case or appeal, as a lead.",
          }),
          reviewedAutomod: Count.meta({
            description: "Of their case and appeal decisions, how many reviewed an automod label.",
          }),
          agreedWithAutomod: Count.meta({
            description: "Of those, how many lead to the same result as automod's label.",
          }),
          overturned: Count.meta({
            description:
              "Of their decisions in the period, how many another moderator has since changed: a later review of the same input, or a lead's relabel.",
          }),
        }),
      )
      .meta({ description: "Each moderator who decided anything, most cases and appeals first." }),
    people: z.array(ProfileSummary).meta({
      description: "The moderators and the people banned or close to a ban, to name them.",
    }),
  })
  .meta({ id: "ModerationStats", description: "How one surface's moderation went over a period." });
export type ModerationStats = z.infer<typeof ModerationStats>;

export const UserParams = z.strictObject({
  surface: ModeratedSurface,
  userId: z.uuid().meta({ description: "The user's account.", example: EXAMPLE.profileId }),
});

export const UserBan = z
  .strictObject({
    id: z.uuid().meta({ example: EXAMPLE.banId }),
    escalationStep: z.int().min(1).meta({
      description: "Which ban on the surface this was within six months, from 1.",
    }),
    startsAt: Timestamp,
    endsAt: Timestamp.nullable().meta({ description: "Null on a permanent ban." }),
    pointsWindow: z.strictObject({ start: Timestamp, end: Timestamp }).meta({
      description:
        "The window whose points crossed the threshold. Judgements in it caused the ban.",
    }),
    lifted: z
      .strictObject({ at: Timestamp, reason: BanLiftReason, by: z.uuid().nullable() })
      .nullable()
      .meta({ description: "When, why, and by whom the ban was lifted early, or null." }),
    inForce: z.boolean(),
  })
  .meta({ id: "UserBan", description: "One surface ban, lifted or not." });
export type UserBan = z.infer<typeof UserBan>;

export const UserModeration = z
  .strictObject({
    user: ProfileSummary.nullable().meta({
      description: "The user, or null once their account is deleted.",
    }),
    surface: ModeratedSurface,
    points: z.strictObject({
      current: Count.meta({ description: "Points given within the window now." }),
      banAt: z.int().min(1).meta({
        description: "Points within the window at which the surface bans the user.",
      }),
      windowStart: Timestamp.meta({ description: "Points given after this count." }),
    }),
    bans: z.array(UserBan).meta({ description: "Every ban on this surface, newest first." }),
    reports: z.array(ReportCaseSummary).meta({
      description:
        "The 20 most recent report cases about items the user answers for on this surface, newest first.",
    }),
    appeals: z.array(Appeal).meta({
      description: "The user's 20 most recent appeals on this surface, newest first.",
    }),
    people: z.array(ProfileSummary).meta({
      description:
        "The moderators who lifted bans, sent cases to a lead, or decided them, and the accounts that answer for the reported items, to name them.",
    }),
  })
  .meta({
    id: "UserModeration",
    description: "One user's standing on one surface, as moderators see it.",
  });
export type UserModeration = z.infer<typeof UserModeration>;

export const ListUserJudgementsQuery = PaginationQuery.extend({
  only: z.enum(["consequences"]).optional().meta({
    description:
      "`consequences` for judgements that refused, took down, or gave points, voided ones included. Omit for every judgement.",
  }),
});
export type ListUserJudgementsQuery = z.infer<typeof ListUserJudgementsQuery>;

export const UserJudgement = z
  .strictObject({
    classification: ModerationClassification,
    snapshot: ModerationSnapshot.meta({ description: "What was judged." }),
    label: z.string().nullable().meta({
      description:
        "What was judged in a few words: the start of its text, or a title. Null for a file.",
    }),
    files: z.array(SnapshotFile).meta({ description: "Every file the snapshot names." }),
    replacedBy: ClassificationId.nullable().meta({
      description:
        "The later classification of the same content that replaced this one: a report or appeal decision, or a reclassification. Only an unreplaced judgement can be reclassified.",
    }),
  })
  .meta({
    id: "UserJudgement",
    description: "One classification of something the user answers for, with what it judged.",
  });
export type UserJudgement = z.infer<typeof UserJudgement>;

export const ListUserJudgementsResponse = z.strictObject({
  judgements: z.array(UserJudgement).meta({ description: "Most recent first." }),
  people: z.array(ProfileSummary).meta({
    description: "The moderators who judged them, to name them.",
  }),
  nextCursor: z.string().nullable(),
});

export const ListModeratedUsersQuery = z.strictObject({
  q: z.string().trim().min(1).max(64).optional().meta({
    description:
      "A full user id for an exact match, or the start of a username or display name, matched case-insensitively. Omit for the users most recently given a consequence.",
    example: "ell",
  }),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListModeratedUsersQuery = z.infer<typeof ListModeratedUsersQuery>;

export const SurfaceStanding = z
  .strictObject({
    surface: ModeratedSurface,
    points: Count.meta({ description: "Points given within the window now." }),
    banAt: z
      .int()
      .min(1)
      .meta({ description: "Points within the window at which the surface bans." }),
    banEndsAt: Timestamp.nullable().meta({
      description: "When the ban in force ends, or null when none is.",
    }),
    banned: z
      .boolean()
      .meta({ description: "Whether a ban is in force, permanent ones included." }),
    lastActionAt: Timestamp.nullable().meta({
      description: "When the user was last given a consequence on the surface.",
    }),
  })
  .meta({ id: "SurfaceStanding", description: "A user's standing on one surface, in short." });
export type SurfaceStanding = z.infer<typeof SurfaceStanding>;

export const ModeratedUser = z
  .strictObject({
    user: ProfileSummary,
    standing: z.array(SurfaceStanding).meta({
      description: "One entry per surface the caller moderates, in the order surfaces are listed.",
    }),
    fullSiteBanned: z.boolean().meta({ description: "Whether a full-site ban is in force." }),
    lastActionAt: Timestamp.nullable().meta({
      description: "The latest `lastActionAt` across those surfaces.",
    }),
  })
  .meta({ id: "ModeratedUser", description: "A user with their standing on each surface." });
export type ModeratedUser = z.infer<typeof ModeratedUser>;

export const ListModeratedUsersResponse = z.strictObject({
  users: z.array(ModeratedUser).meta({
    description:
      "With `q`, matches by display name; without, the users most recently given a consequence, newest first.",
  }),
});

export const ClassificationIdParams = z.strictObject({ classificationId: ClassificationId });

export const ReclassifyRequest = z
  .strictObject({
    categories: z.array(CategoryAssessment).min(1).meta({
      description: "The lead moderator's categorization of what the classification judged.",
    }),
    mandatoryTags: MandatoryTags.optional().meta({
      description:
        "Every mandatory tag the asset's file calls for, when the classification judged one. Leave it out to keep the replaced classification's. A published asset takes them on.",
    }),
    takeDownWholeAsset: z.boolean().optional().meta({
      description:
        "On an asset's classification, `true` takes the whole asset down if the new ruling removes the judged version: every version leaves public view, the asset leaves discovery and every collection, and only its owner can still read it. Without it, only the judged version is removed. Any other classification refuses it.",
      example: false,
    }),
  })
  .meta({
    id: "ReclassifyRequest",
    example: { categories: [{ category: "appropriate", severity: null }] },
  });
export type ReclassifyRequest = z.infer<typeof ReclassifyRequest>;

export const FullSiteBanId = ModerationId("The full-site ban's id.", EXAMPLE.banId);

export const FullSiteBan = z
  .strictObject({
    id: FullSiteBanId,
    userId: z.uuid().meta({ description: "The banned account.", example: EXAMPLE.profileId }),
    note: z.string().meta({ description: "Why, as the staff member wrote it. Staff only." }),
    placedBy: z.uuid().meta({ example: EXAMPLE.moderatorId }),
    startsAt: Timestamp,
    lifted: z
      .strictObject({ at: Timestamp, by: z.uuid() })
      .nullable()
      .meta({ description: "When and by whom the ban was lifted, or null while it stands." }),
  })
  .meta({
    id: "FullSiteBan",
    description:
      "A permanent ban from the whole site, placed by hand. It signs the account out everywhere and stops every later sign-in.",
    example: {
      id: EXAMPLE.banId,
      userId: EXAMPLE.profileId,
      note: "Repeated hate symbols across three collections after two asset bans.",
      placedBy: EXAMPLE.moderatorId,
      startsAt: EXAMPLE.openedAt,
      lifted: null,
    },
  });
export type FullSiteBan = z.infer<typeof FullSiteBan>;

export const FullSiteBanResponse = z.strictObject({ ban: FullSiteBan });

export const FullSiteBanIdParams = z.strictObject({ banId: FullSiteBanId });

export const CreateFullSiteBanRequest = z
  .strictObject({
    userId: z.uuid().meta({ description: "The account to ban.", example: EXAMPLE.profileId }),
    note: boundedText(1000).meta({
      description: "Why, for staff who read it later; 1–1,000 Unicode code points.",
    }),
  })
  .meta({
    id: "CreateFullSiteBanRequest",
    example: {
      userId: EXAMPLE.profileId,
      note: "Repeated hate symbols across three collections after two asset bans.",
    },
  });
export type CreateFullSiteBanRequest = z.infer<typeof CreateFullSiteBanRequest>;

export const ListFullSiteBansQuery = z.strictObject({
  userId: z.uuid().meta({ description: "The account whose bans to list." }),
});
export type ListFullSiteBansQuery = z.infer<typeof ListFullSiteBansQuery>;

export const ListFullSiteBansResponse = z.strictObject({
  bans: z
    .array(FullSiteBan)
    .meta({ description: "Every full-site ban the account has had, newest first." }),
  people: z.array(ProfileSummary).meta({
    description:
      "The staff who placed and lifted the bans, to name them. A deleted account is left out.",
  }),
});
